import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../../lib/output.js';
import { MutagentError, ApiError, handleError } from '../../lib/errors.js';
import {
  promptsDashboardLink, evaluationLinks, evaluationLink,
  formatCreationHints,
  evaluationCreatedDirective, evaluationDeletedDirective,
  echoDirectiveToStderr,
} from '../../lib/ui-links.js';
import { resolveNumericPromptId } from '../../lib/resolve-prompt-id.js';
import type { ResourceState } from '../../lib/ui-links.js';
import { updateMutationContext, type DeleteOptions } from './index.js';
import { buildGuidedWorkflow, buildGenericEvalWorkflow, type GuidedWorkflowResult } from './guided-workflow.js';
import {
  cliCriteriaArrayToCanonical,
  canonicalCriteriaArrayToCli,
  type CliCriterion,
} from '../../lib/adapters/eval-criteria.js';
import { registerEvaluationRunCommand } from './evaluations-run.js';

interface EvaluationCreateOptions {
  data?: string;
  name?: string;
  description?: string;
  guided?: boolean;
}

export function registerEvaluationCommands(prompts: Command): void {
  const evaluation = new Command('evaluation')
    .description('Manage evaluations for prompts')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts evaluation list <prompt-id>
  ${chalk.dim('$')} mutagent prompts evaluation get <evaluation-id>
  ${chalk.dim('$')} mutagent prompts evaluation create <prompt-id> --name "My Eval"
  ${chalk.dim('$')} mutagent prompts evaluation delete <evaluation-id>
    `)
    .action(() => {
      evaluation.help();
    });
  prompts.addCommand(evaluation);

  // List evaluations
  evaluation
    .command('list')
    .description('List evaluations for a prompt')
    .argument('<prompt-id>', 'Prompt ID (from: mutagent prompts list)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts evaluation list <prompt-id>
  ${chalk.dim('$')} mutagent prompts evaluation list <prompt-id> --json
    `)
    .action(async (promptId: string) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const evals = await client.listEvaluations(promptId);

        if (isJson) {
          // Summary view — only IDs and key fields. Use `prompts evaluation get` for full details.
          const withLinks = evals.map((e) => ({
            id: e.id,
            name: e.name,
            description: e.description,
            updatedAt: e.updatedAt,
            _links: evaluationLinks(promptId, e.id),
          }));
          output.output(withLinks);
        } else {
          const formatted = evals.map((e) => ({
            id: e.id,
            name: e.name,
            description: e.description,
            updated: e.updatedAt ? new Date(String(e.updatedAt)).toLocaleDateString() : 'N/A',
            url: evaluationLink(promptId, e.id),
          }));
          output.output(formatted);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Get evaluation details
  evaluation
    .command('get')
    .description('Get evaluation details including criteria')
    .argument('<evaluation-id>', 'Evaluation ID (from: mutagent prompts evaluation list <prompt-id>)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts evaluation get <evaluation-id>
  ${chalk.dim('$')} mutagent prompts evaluation get <evaluation-id> --json
    `)
    .action(async (evaluationId: string) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const client = await getSDKClient();
        const evalObj = await client.getEvaluation(evaluationId);

        // Resolve numeric promptId from the evaluation's promptGroupId (UUID).
        // The UI route /prompts/:id expects the numeric id, not the UUID.
        // Orphaned evaluations (no prompts in group) fall back to the prompts dashboard.
        const numericPromptId = await resolveNumericPromptId(client, evalObj.promptGroupId);
        if (numericPromptId === null) {
          // Debug-only warning; never throw.
          if (process.env.DEBUG) {
            console.error(
              `[debug] Evaluation ${String(evalObj.id)} references promptGroupId ${evalObj.promptGroupId} but no prompts were found in that group. Falling back to prompts dashboard.`,
            );
          }
        }

        if (isJson) {
          // B3: compute runnable flag (has at least one criterion)
          const evalConfigForRunnable = evalObj.evalConfig as { criteria?: unknown[] } | null | undefined;
          const criteriaForRunnable = evalConfigForRunnable?.criteria;
          const runnable = Array.isArray(criteriaForRunnable) && criteriaForRunnable.length > 0;

          output.output({
            ...evalObj,
            runnable,
            _links: numericPromptId !== null
              ? evaluationLinks(numericPromptId, evalObj.id)
              : { dashboard: promptsDashboardLink(), api: `/api/prompts/${evalObj.promptGroupId}/evaluations/${String(evalObj.id)}` },
          });
        } else {
          output.success(`Evaluation: ${evalObj.name} (ID: ${String(evalObj.id)})`);
          if (evalObj.description) {
            console.log(`  Description: ${evalObj.description}`);
          }
          console.log(`  Created:     ${evalObj.createdAt ? new Date(String(evalObj.createdAt)).toLocaleDateString() : 'N/A'}`);
          console.log('');

          // Display criteria — BE returns canonical shape; adapt to CLI shape for display
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const config = evalObj.evalConfig as { criteria?: any[] } | null;
          const criteria: CliCriterion[] = canonicalCriteriaArrayToCli(config?.criteria);
          if (criteria.length > 0) {
            console.log('  Criteria:');
            for (const c of criteria) {
              console.log(`    ${chalk.cyan(c.name)}`);
              if (c.description) {
                console.log(`      Description: ${c.description}`);
              }
              if (c.evaluationParameter) {
                console.log(`      Parameter:   ${c.evaluationParameter}`);
              }
              console.log('');
            }
          } else {
            console.log('  No criteria defined.');
          }

          // Display LLM config if present
          const llmConfig = evalObj.llmConfig as Record<string, unknown> | null;
          if (llmConfig && Object.keys(llmConfig).length > 0) {
            console.log('  LLM Config:');
            for (const [key, value] of Object.entries(llmConfig)) {
              console.log(`    ${key}: ${String(value)}`);
            }
            console.log('');
          }

          const dashboardUrl = numericPromptId !== null
            ? evaluationLink(numericPromptId, evalObj.id)
            : promptsDashboardLink();
          output.info(`View in dashboard: ${dashboardUrl}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Create evaluation
  evaluation
    .command('create')
    .description('Create an evaluation configuration for a prompt')
    .argument('[prompt-id]', 'Prompt ID (from: mutagent prompts list) — optional when --guided is set')
    .option('-d, --data <json>', 'Evaluation as JSON string (for pre-validated criteria only)')
    .option('-n, --name <name>', 'Evaluation name (required unless --guided)')
    .option('--description <text>', 'Evaluation description')
    .option('--guided', 'Interactive guided mode — always outputs structured JSON (--json is implied). prompt-id is optional when --guided is set.')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts evaluation create --guided --json               ${chalk.dim('# no prompt-id: returns generic workflow + upload instructions')}
  ${chalk.dim('$')} mutagent prompts evaluation create <prompt-id> --guided          ${chalk.dim('# recommended: shows workflow guide + schema fields')}
  ${chalk.dim('$')} mutagent prompts evaluation create <prompt-id> --guided --json  ${chalk.dim('# structured workflow for AI agents')}
  ${chalk.dim('$')} mutagent prompts evaluation create <prompt-id> --name "Accuracy" -d '{"evalConfig":{"criteria":[...]}}'  ${chalk.dim('# power user')}

Guided Workflow (recommended):
  --guided outputs a workflow guide that:
    1. Fetches your prompt's inputSchema + outputSchema fields
    2. Shows available fields for evaluation criteria
    3. Provides a criteria template and example command
    4. For AI agents: includes a _directive with workflow instructions

  Works in all modes (TTY, non-TTY, --json).

AI Agent (MANDATORY):
  1. Run: mutagent prompts evaluation create --help
  2. Run: mutagent prompts evaluation create <id> --guided --json
  3. Parse askUserQuestions from the JSON output
  4. Use AskUserQuestion to collect DETAILED rubrics from the user
  5. NEVER auto-generate criteria. NEVER use vague rubrics.
  6. Construct --data JSON from user answers, then:
     mutagent prompts evaluation create <id> --name "<name>" -d '<json>' --json

Expected Criteria Shape (--data):
  ${chalk.dim('{"evalConfig":{"criteria":[{"name":"<name>","description":"<scoring rubric>","evaluationParameter":"<schema field>"}]}}')}
  evaluationParameter must target an outputSchema OR inputSchema field.

${chalk.red('Required: --name (unless --guided). Criteria must include evaluationParameter.')}
${chalk.dim('CLI flags (--name, --description) override --data fields.')}
${chalk.dim('Get prompt IDs: mutagent prompts list')}
${chalk.cyan('Conceptual help: .claude/skills/mutagent-cli/concepts/eval-criteria.md')} ${chalk.dim('(install via mutagent skills install)')}
    `)
    .action(async (promptId: string | undefined, options: EvaluationCreateOptions) => {
      // --guided always implies --json — agents must never use --guided without --json
      let isJson = getJsonFlag(prompts);
      if (options.guided) {
        isJson = true;
      }
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // --guided without prompt-id: return generic workflow template
        if (options.guided && !promptId) {
          const result = buildGenericEvalWorkflow();
          output.output(result);
          return;
        }

        // Non-guided without prompt-id: error
        if (!promptId) {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'prompt-id is required',
            'Run: mutagent prompts evaluation create --help\n' +
            'Usage: mutagent prompts evaluation create <prompt-id> [options]\n' +
            'With --guided: mutagent prompts evaluation create --guided --json (returns generic workflow)'
          );
        }

        // Render the guided workflow (shared between --guided and auto-trigger)
        const renderGuidedWorkflow = (wf: GuidedWorkflowResult): void => {
          const { inputFields, outputFields, allFields } = wf._meta;
          if (isJson) {
            const { _meta: _discarded, ...cleanWorkflow } = wf;
            void _discarded;
            output.output(cleanWorkflow);
            echoDirectiveToStderr(wf._directive);
          } else {
            output.success(`Guided Evaluation Workflow for prompt: ${wf.prompt.name} (ID: ${promptId})`);
            console.log('');
            if (outputFields.length > 0) {
              console.log(`  Output Schema Fields: ${outputFields.join(', ')}`);
            }
            if (inputFields.length > 0) {
              console.log(`  Input Schema Fields:  ${inputFields.join(', ')}`);
            }
            console.log('');
            console.log('  For each field, define what correct output looks like:');
            for (const { field, source } of allFields) {
              console.log(`    ${chalk.cyan(field)} (${source})`);
              console.log(`      → What makes a correct vs incorrect "${field}"?`);
            }
            console.log('');
            console.log('  Then create the evaluation:');
            console.log(`    mutagent prompts evaluation create ${promptId} --name "My Eval" \\`);
            console.log(`      -d '{"evalConfig":{"criteria":[{"name":"accuracy","description":"Correct if output matches expected structure","evaluationParameter":"${outputFields[0] ?? '<field>'}"}]}}'`);
            console.log('');
          }
        };

        // Guided mode: dump structured workflow/template (non-interactive)
        if (options.guided) {
          const workflow = await buildGuidedWorkflow(promptId);
          renderGuidedWorkflow(workflow);
          return;
        }

        // No criteria source — auto-return guided workflow in --json/TTY mode
        if (!options.data) {
          if (isJson || process.stdout.isTTY) {
            const workflow = await buildGuidedWorkflow(promptId);
            renderGuidedWorkflow(workflow);
            return;
          }
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'No criteria source provided',
            'Run: mutagent prompts evaluation create --help\n' +
            'Then: mutagent prompts evaluation create ' + promptId + ' --guided --json'
          );
        }

        // Non-guided: --name is required
        if (!options.name) {
          throw new MutagentError(
            'MISSING_ARGUMENTS',
            'Evaluation name is required',
            'Run: mutagent prompts evaluation create --help\n' +
            '[Agent: Ask the user for an evaluation name via AskUserQuestion, then pass --name]\n' +
            'Use --name <name>, e.g., mutagent prompts evaluation create ' + promptId + ' --name "My Eval" --data \'...\''
          );
        }

        const evalData: {
          name: string;
          description?: string;
          evalConfig?: Record<string, unknown>;
          llmConfig?: Record<string, unknown>;
          tags?: string[];
          metadata?: Record<string, unknown>;
        } = {
          name: options.name,
          description: options.description,
        };

        // Parse inline JSON
        try {
          const parsed = JSON.parse(options.data) as Record<string, unknown>;
          if (parsed.evalConfig) evalData.evalConfig = parsed.evalConfig as Record<string, unknown>;
          if (parsed.llmConfig) evalData.llmConfig = parsed.llmConfig as Record<string, unknown>;
          if (parsed.tags) evalData.tags = parsed.tags as string[];
          if (parsed.metadata) evalData.metadata = parsed.metadata as Record<string, unknown>;
        } catch {
          throw new MutagentError(
            'INVALID_JSON',
            'Invalid JSON in --data flag',
            'Run: mutagent prompts evaluation create --help\n' +
            'Provide a valid JSON object, e.g., \'{"evalConfig":{"criteria":[...]},"llmConfig":{"model":"gpt-4"}}\''
          );
        }

        // CLI flags override --data fields
        if (options.name) evalData.name = options.name;
        if (options.description) evalData.description = options.description;

        // Validate evaluation criteria exist and are well-formed (B2: --data defense)
        // Empty or missing criteria is EVAL_INCOMPLETE — reject client-side before any API call.
        const criteria = evalData.evalConfig?.criteria;
        if (!criteria || !Array.isArray(criteria) || criteria.length === 0) {
          // Always throw — suggest --guided for workflow
          let fieldsHint = '';
          try {
            const client = await getSDKClient();
            const prompt = await client.getPrompt(promptId);
            if (prompt.outputSchema && typeof prompt.outputSchema === 'object') {
              const props = (prompt.outputSchema as Record<string, unknown>).properties;
              if (props && typeof props === 'object') {
                const fields = Object.keys(props as Record<string, unknown>);
                if (fields.length > 0) {
                  fieldsHint = `\nDetected output fields from prompt schema: ${fields.join(', ')}\n`;
                }
              }
            }
          } catch {
            // Best-effort — don't block on fetch failure
          }
          throw new MutagentError(
            'EVAL_INCOMPLETE',
            'Evaluation criteria are required and cannot be empty. Provide at least one criterion via --data.',
            'Run: mutagent prompts evaluation create --help\n' +
            'Then: mutagent prompts evaluation create ' + promptId + ' --guided --json\n' +
            fieldsHint +
            '\nExample JSON (--data flag):\n' +
            '  --data \'{"evalConfig":{"criteria":[{"name":"Accuracy","description":"...","evaluationParameter":"classification"}]}}\'\n'
          );
        }
        for (const c of criteria as Record<string, unknown>[]) {
          // Accept both new format (description/evaluationParameter) and legacy (scoringRubric/targetField)
          // Normalize legacy fields to new format for storage FIRST
          if (c.scoringRubric && !c.description) {
            c.description = c.scoringRubric;
            delete c.scoringRubric;
          }
          if (c.targetField && !c.evaluationParameter) {
            const tf = c.targetField as string;
            c.evaluationParameter = tf.startsWith('output.') ? tf.slice('output.'.length)
              : tf.startsWith('input.') ? tf.slice('input.'.length) : tf;
            delete c.targetField;
          }

          const hasDescription = c.description;
          const hasEvalParam = c.evaluationParameter;
          if (!c.name || !hasDescription || !hasEvalParam) {
            const cName = typeof c.name === 'string' ? c.name : 'unnamed';
            throw new MutagentError(
              'VALIDATION_ERROR',
              `Criterion "${cName}" is missing required fields. Need: name, description, evaluationParameter.`,
              'Run: mutagent prompts evaluation create --help\n' +
              'Then: mutagent prompts evaluation create ' + promptId + ' --guided --json'
            );
          }
        }

        // Detect duplicate evaluationParameter values
        const seenParams = new Set<string>();
        const duplicateParams: string[] = [];
        for (const c of criteria as { evaluationParameter?: string }[]) {
          const param = c.evaluationParameter;
          if (param) {
            if (seenParams.has(param)) {
              duplicateParams.push(param);
            }
            seenParams.add(param);
          }
        }
        if (duplicateParams.length > 0) {
          // Best-effort: fetch prompt outputSchema to show available fields
          let availableFields: string[] = [];
          try {
            const client = await getSDKClient();
            const prompt = await client.getPrompt(promptId);
            if (prompt.outputSchema && typeof prompt.outputSchema === 'object') {
              const props = (prompt.outputSchema as Record<string, unknown>).properties;
              if (props && typeof props === 'object') {
                availableFields = Object.keys(props as Record<string, unknown>);
              }
            }
          } catch {
            // Best-effort — don't block on fetch failure
          }
          const uniqueDupes = [...new Set(duplicateParams)];
          const fieldsHint = availableFields.length > 0
            ? `\nAvailable output fields: ${availableFields.join(', ')}`
            : '';
          throw new MutagentError(
            'VALIDATION_ERROR',
            `Duplicate evaluationParameter: "${uniqueDupes.join('", "')}". Each criterion must target a unique output field.`,
            'Run: mutagent prompts evaluation create --help\n' +
            'Each criterion scores a different output field. Fix by changing the evaluationParameter to a unique field.' +
            fieldsHint +
            '\n\nExample:\n  --data \'{"evalConfig":{"criteria":[' +
            '{"name":"Accuracy","description":"...","evaluationParameter":"classification"},' +
            '{"name":"Confidence","description":"...","evaluationParameter":"confidence"}' +
            ']}}\''
          );
        }

        // Validate criteria cover ALL schema fields (input + output)
        const schemaClient = await getSDKClient();
        const prompt = await schemaClient.getPrompt(promptId);
        const requiredFields: { field: string; source: string }[] = [];

        if (prompt.inputSchema && typeof prompt.inputSchema === 'object') {
          const props = (prompt.inputSchema as Record<string, unknown>).properties;
          if (props && typeof props === 'object') {
            for (const field of Object.keys(props as Record<string, unknown>)) {
              requiredFields.push({ field, source: 'inputSchema' });
            }
          }
        }
        if (prompt.outputSchema && typeof prompt.outputSchema === 'object') {
          const props = (prompt.outputSchema as Record<string, unknown>).properties;
          if (props && typeof props === 'object') {
            for (const field of Object.keys(props as Record<string, unknown>)) {
              requiredFields.push({ field, source: 'outputSchema' });
            }
          }
        }

        if (requiredFields.length > 0) {
          const coveredParams = new Set(
            (criteria as { evaluationParameter?: string }[]).map(c => c.evaluationParameter)
          );
          const missing = requiredFields.filter(f => !coveredParams.has(f.field));
          if (missing.length > 0) {
            const missingList = missing.map(m => `${m.field} (${m.source})`).join(', ');
            throw new MutagentError(
              'VALIDATION_ERROR',
              `Evaluation criteria are INCOMPLETE. Missing fields: ${missingList}`,
              'Each input and output schema field MUST have a corresponding criterion.\n' +
              `Total required: ${requiredFields.length}, provided: ${coveredParams.size}\n` +
              'Run: mutagent prompts evaluation create ' + promptId + ' --guided --json'
            );
          }
        }

        // Validate evaluationParameter values match actual schema fields
        if (requiredFields.length > 0) {
          const validFieldNames = new Set(requiredFields.map(f => f.field));
          const invalidParams = (criteria as { evaluationParameter?: string; name?: string }[])
            .filter(c => c.evaluationParameter && !validFieldNames.has(c.evaluationParameter));
          if (invalidParams.length > 0) {
            const invalidList = invalidParams.map(c => `"${c.evaluationParameter}" (criterion: ${c.name})`).join(', ');
            throw new MutagentError(
              'VALIDATION_ERROR',
              `Invalid evaluationParameter(s): ${invalidList}. Must match a schema field.`,
              `Available fields: ${requiredFields.map(f => f.field).join(', ')}\n` +
              'Run: mutagent prompts evaluation create ' + promptId + ' --guided --json'
            );
          }
        }

        // Adapt validated CLI-shaped criteria to canonical wire/storage shape
        // BEFORE sending to the BE. CLI input keeps {name, description,
        // evaluationParameter} for the user; canonical {criteria, ...} on the wire.
        if (evalData.evalConfig && Array.isArray((evalData.evalConfig as { criteria?: unknown }).criteria)) {
          const cliCriteria = (evalData.evalConfig as { criteria: CliCriterion[] }).criteria;
          (evalData.evalConfig as { criteria: unknown }).criteria =
            cliCriteriaArrayToCanonical(cliCriteria);
        }

        const client = await getSDKClient();
        const evalResult = await client.createEvaluation(promptId, evalData);

        if (isJson) {
          const criteriaLen = (evalData.evalConfig?.criteria as unknown[] | undefined)?.length ?? 0;
          let rsState: ResourceState | undefined;
          try {
            const datasets = await client.listDatasets(promptId);
            rsState = { datasets: datasets.length };
          } catch { /* non-critical */ }
          const directive = evaluationCreatedDirective(promptId, evalResult.id, evalResult.name, criteriaLen, rsState);
          output.output({
            ...evalResult,
            _links: evaluationLinks(promptId, evalResult.id),
            _directive: directive,
          });
          echoDirectiveToStderr(directive);
        } else {
          output.success(`Created evaluation: ${evalResult.name} (id: ${String(evalResult.id)})`);
          const hints = formatCreationHints({
            resourceType: 'Evaluation',
            id: evalResult.id,
            name: evalResult.name,
            dashboardUrl: evaluationLink(promptId, evalResult.id),
            apiPath: `/api/prompts/${promptId}/evaluations/${String(evalResult.id)}`,
          });
          console.log(hints);
        }

        // Auto-update mutation context
        const criteriaCount = (evalData.evalConfig?.criteria as unknown[] | undefined)?.length ?? 0;
        updateMutationContext(ctx => {
          ctx.addEvaluation(evalResult.name, criteriaCount, String(evalResult.id), promptId);
        });
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Delete evaluation
  evaluation
    .command('delete')
    .description('Delete an evaluation')
    .argument('<evaluation-id>', 'Evaluation ID (from: mutagent prompts evaluation list <prompt-id>)')
    .option('--force', 'Skip confirmation')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts evaluation delete <evaluation-id>
  ${chalk.dim('$')} mutagent prompts evaluation delete <evaluation-id> --force
  ${chalk.dim('$')} mutagent prompts evaluation delete <evaluation-id> --force --json
    `)
    .action(async (evaluationId: string, options: DeleteOptions) => {
      const isJson = getJsonFlag(prompts);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // In --json mode, auto-skip confirmation. Otherwise require --force.
        if (!options.force && !isJson) {
          throw new MutagentError(
            'CONFIRMATION_REQUIRED',
            `Deleting evaluation ${evaluationId} requires confirmation`,
            `Run: mutagent prompts evaluation delete --help\n` +
            `[Agent: Ask the user to confirm via MultiChoice, then pass --force]\n` +
            `Use --force to confirm: mutagent prompts evaluation delete ${evaluationId} --force`
          );
        }

        const client = await getSDKClient();
        try {
          await client.deleteEvaluation(evaluationId);
        } catch (deleteError) {
          // 404 = already deleted = idempotent success
          if (deleteError instanceof ApiError && deleteError.statusCode === 404) {
            if (isJson) {
              const directive = evaluationDeletedDirective(evaluationId);
              output.output({ success: true, deletedId: evaluationId, _links: { dashboard: promptsDashboardLink() }, _directive: directive });
              echoDirectiveToStderr(directive);
            } else {
              output.success(`Evaluation ${evaluationId} already deleted (idempotent)`);
            }
            return;
          }
          throw deleteError;
        }

        if (isJson) {
          const directive = evaluationDeletedDirective(evaluationId);
          output.output({ success: true, deletedId: evaluationId, _links: { dashboard: promptsDashboardLink() }, _directive: directive });
          echoDirectiveToStderr(directive);
        } else {
          output.success(`Evaluation ${evaluationId} deleted successfully`);
          output.info(`View evaluations: ${promptsDashboardLink()}`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Run evaluation (playground eval) - extracted to evaluations-run.ts to stay under 500-line limit
  registerEvaluationRunCommand(evaluation, prompts);
}
